import { isNotNullish } from '@airreading/common';
import { GetSessionFeedbackOptions } from '@airreading/common/data';

export const toInitialsUppercase = (fullName: string) => {
  return fullName
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase())
    .join('');
};

export const textAreaValueToArray = (textAreaValue?: string | null) => {
  if (!textAreaValue) return [];

  // Split the text area value using a regular expression that matches line breaks for all types of OS (\r\n, \r, \n)
  const lines = textAreaValue.split(/\r\n|\r|\n/);

  return lines
    .filter(isNotNullish)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

export const commaSeparatedValueToArray = (value?: string | null) => {
  if (!value) return [];

  return value
    .split(',')
    .filter(isNotNullish)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

export const parseSessionFeedbackCompoundId = (compoundId: string): GetSessionFeedbackOptions => {
  const [sessionId, studentId, coachId] = compoundId.split('__');
  return { sessionId, studentId, coachId };
};

export const internationalPhoneRegex = /^\+\d{1,3}([-\s]?\d+)+$/;

export const disallowedEmailDomains = ['@airreadingclass.com', '@teachwithair.com'];

export const zoomIdValidationRegex = /^\d{9,11}$/;

export const zoomIdExtractRegex = /j\/(\d+)/;
export const minZoomIdLength = 9;
export const maxZoomIdLength = 11;

/**
 * Converts a string value of 'null' or an empty string to `null`, otherwise returns the original value.
 *
 * @example
 * ```typescript
 * nullIfString('null'); // null
 * nullIfString(''); // null
 * nullIfString('hello'); // 'hello'
 * nullIfString(null); // null
 * ```
 */
export const nullIfString = <T extends string | null>(value: T): Exclude<T, 'null'> | null =>
  value === 'null' || value === '' ? null : (value as Exclude<T, 'null'>);

/**
 * Converts `null`, `undefined`, or an empty string to the string 'null', otherwise returns the original value.
 *
 * @example
 * ```typescript
 * stringIfNull(null); // 'null'
 * stringIfNull(undefined); // 'null'
 * stringIfNull(''); // 'null'
 * stringIfNull('hello'); // 'hello'
 * ```
 */
export const stringIfNull = <T extends string | null | undefined>(value: T): Exclude<T, null> | 'null' =>
  !value ? 'null' : (value as Exclude<T, null>);

export const booleanToCustomString = (
  value: boolean | null | undefined,
  whenTrue = 'Yes',
  whenFalse = 'No',
): string | null | undefined => {
  if (value === true) return whenTrue;
  if (value === false) return whenFalse;
  return value;
};

export function pluralize(count: number, one: string, many: string) {
  return count === 1 ? one : many;
}

/**
 * Normalize a string so newline breaks render properly in HTML/React.
 *
 * - Converts CRLF/CR to LF.
 * - Converts *literal* escape sequences (`\\n`, `\\r`, `\\r\\n`) to real newlines.
 *
 * @param {string} value - The original text (may contain literal `\n` sequences).
 * @returns {string} Normalized text using real `\n` line breaks.
 */
export function normalizeNewlines(value: string): string {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\r\\n|\\n|\\r/g, '\n');
}
